import { Box, Center, Text, Button, HStack, VStack, Icon } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect } from "react";
import dayjs from "dayjs";
import { IoSearchOutline, IoAlertCircleOutline } from "react-icons/io5";
import Loader from "../../../loader";
import { downloadExport, getExportCount, resetExportState } from "../../../../store/actions/exportActions";
import { formatNumber } from "../../../../store/helpers";
import EnterpriseReportSummary from "../../../reports/EnterpriseReportSummary";
import { buildExportDataSummary } from "../../../reports/ticketInfoEnterpriseSummary";

const BranchExportData = () => {
  const dispatch = useDispatch();
  const { exportCount, exportError, hasSearched, isFetchingExport, isDownloadingExport, timeUnit } =
    useSelector((state) => state.exportReducer);
  const { dateRange, organizationId, branchId, branches } = useSelector(
    (state) => state.defaultReducer
  );

  // Reset export state when filters change
  useEffect(() => {
    dispatch(resetExportState());
  }, [branchId, dateRange, dispatch]);

  const handleRetry = () => {
    dispatch(
      getExportCount({
        organizationId,
        branchId,
        createdFrom: dateRange[0],
        createdTo: dateRange[1],
      })
    );
  };

  const handleDownload = () => {
    dispatch(
      downloadExport({
        organizationId,
        branchId,
        createdFrom: dateRange[0],
        createdTo: dateRange[1],
        timeUnit,
      })
    );
  };

  // Format the period display
  const periodDisplay = dateRange[0]
    ? dayjs.unix(dateRange[0]).format("MMMM YYYY")
    : "Select a month";

  // Get branch name for display
  const selectedBranch = branches.find((b) => b.id === branchId);
  const branchDisplay = branchId ? selectedBranch?.name || "Selected Branch" : "All Branches";
  const exportSummary = buildExportDataSummary({
    branchDisplay,
    exportCount,
    periodDisplay,
    timeUnit,
  });

  // Render loading state
  if (isFetchingExport) {
    return (
      <Box mt={6}>
        <Center mt={10}>
          <VStack gap={4}>
            <Loader />
            <Text color="gray.500">Counting records...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  // Render initial state (no search yet)
  if (!hasSearched) {
    return (
      <Box mt={6}>
        <Center>
          <Box
            mt={10}
            p={8}
            borderRadius={10}
            bg="gray.50"
            w={{ base: "90%", md: "50%" }}
            textAlign="center"
          >
            <Icon as={IoSearchOutline} w={12} h={12} color="gray.400" mb={4} />
            <Text color="gray.600" fontSize="lg" fontWeight="500" mb={2}>
              Select a branch and month, then click Search
            </Text>
            <Text color="gray.400" fontSize="sm">
              Export ticket data as a CSV file for further analysis in Excel or other tools.
            </Text>
          </Box>
        </Center>
      </Box>
    );
  }

  // Render error state (count request failed - not the same as 0 tickets)
  if (hasSearched && exportError) {
    return (
      <Box mt={6}>
        <Center>
          <Box
            mt={10}
            p={8}
            borderRadius={10}
            bg="red.50"
            border="1px solid"
            borderColor="red.200"
            w={{ base: "90%", md: "50%" }}
            textAlign="center"
          >
            <Icon as={IoAlertCircleOutline} w={12} h={12} color="red.400" mb={4} />
            <Text color="red.700" fontSize="lg" fontWeight="600" mb={2}>
              Could not load ticket count
            </Text>
            <Text color="red.600" fontSize="sm" mb={4}>
              {exportError}
            </Text>
            <Button
              borderRadius="8px"
              background="#222"
              color="white"
              _hover={{ bg: "#000" }}
              onClick={handleRetry}
            >
              Retry
            </Button>
          </Box>
        </Center>
      </Box>
    );
  }

  // Render no results state (searched but count is 0)
  if (hasSearched && exportCount === 0) {
    return (
      <Box mt={6}>
        <EnterpriseReportSummary
          title="Export Data Summary"
          kpis={exportSummary.kpis}
          tables={exportSummary.tables}
          notes={exportSummary.notes}
        />
        <Center>
          <Box
            mt={10}
            p={8}
            borderRadius={10}
            bg="orange.50"
            border="1px solid"
            borderColor="orange.200"
            w={{ base: "90%", md: "50%" }}
            textAlign="center"
          >
            <Icon as={IoAlertCircleOutline} w={12} h={12} color="orange.400" mb={4} />
            <Text color="orange.700" fontSize="lg" fontWeight="600" mb={2}>
              No tickets found
            </Text>
            <Text color="orange.600" fontSize="sm" mb={1}>
              There are no tickets for <strong>{branchDisplay}</strong> in <strong>{periodDisplay}</strong>.
            </Text>
            <Text color="gray.500" fontSize="sm" mt={3}>
              Try selecting a different branch or month.
            </Text>
          </Box>
        </Center>
      </Box>
    );
  }

  // Render results with download button
  return (
    <Box mt={6}>
      <EnterpriseReportSummary
        title="Export Data Summary"
        kpis={exportSummary.kpis}
        tables={exportSummary.tables}
        notes={exportSummary.notes}
      />
      <Box
        p={6}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="10px"
        maxW="500px"
      >
        <VStack align="start" gap={4}>
          <HStack justify="space-between" w="100%">
            <Text fontWeight="600" color="gray.600">
              Period:
            </Text>
            <Text fontWeight="700" fontSize="lg">
              {periodDisplay}
            </Text>
          </HStack>

          <HStack justify="space-between" w="100%">
            <Text fontWeight="600" color="gray.600">
              Branch:
            </Text>
            <Text fontWeight="700" fontSize="lg">
              {branchDisplay}
            </Text>
          </HStack>

          <HStack justify="space-between" w="100%">
            <Text fontWeight="600" color="gray.600">
              Records:
            </Text>
            <Text fontWeight="700" fontSize="lg" color="green.600">
              {formatNumber(exportCount)} tickets
            </Text>
          </HStack>

          <Button
            w="100%"
            h="56px"
            mt={4}
            borderRadius="8px"
            background="#222"
            color="white"
            _hover={{ bg: "#000" }}
            _disabled={{ bg: "gray.400", cursor: "not-allowed" }}
            onClick={handleDownload}
            disabled={isDownloadingExport}
            loading={isDownloadingExport}
            loadingText="Preparing download..."
          >
            Download CSV
          </Button>

          {isDownloadingExport && (
            <Text color="gray.500" fontSize="sm" textAlign="center" w="100%">
              Please wait while we prepare your export...
            </Text>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default BranchExportData;
